import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';


function calculateCost(calls) {
  if (calls <= 100) return 0;
  let remaining = calls - 100;
  let total = 0;

  // 101-10,000: $0.12
  const tier1 = Math.min(remaining, 9900);
  total += tier1 * 0.12;
  remaining -= tier1;

  // 10,001-100,000: $0.10
  if (remaining > 0) {
    const tier2 = Math.min(remaining, 90000);
    total += tier2 * 0.10;
    remaining -= tier2;
  }

  // 100,001+: $0.08
  if (remaining > 0) {
    total += remaining * 0.08;
  }

  return total;
}

function PricingPage() {
  const [calls, setCalls] = useState(1000);
  const estimatedCost = calculateCost(calls);
  const effectiveRate = calls > 100 ? (estimatedCost / (calls - 100)).toFixed(3) : '0.000';

  return (
    <div className="bg-gradient-to-b from-slate-800 to-slate-900 text-white py-16 px-4 sm:px-6 lg:px-8">
      <Helmet>
        <title>Pricing — CommunityGuardAPI | From $0.08/call</title>
        <link rel="canonical" href="https://communityguardapi.com/pricing" />
        <meta name="description" content="Pay-as-you-go sex offender registry API. 100 free calls/month, no contracts. Volume discounts starting at 10K calls." />
        <meta property="og:title" content="Pricing — CommunityGuardAPI | From $0.08/call" />
        <meta property="og:description" content="Pay-as-you-go sex offender registry API. 100 free calls/month, no contracts. Volume discounts starting at 10K calls." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://communityguardapi.com/pricing" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Simple, <span className="text-emerald-500">Transparent</span> Pricing
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto">
            Pay only for what you use. Start with 100 free API calls every month. No contracts, no minimums, no surprises.
          </p>
        </div>

        {/* Pricing Tiers — horizontal scroll on mobile, grid on desktop */}
        <div className="mb-16 -mx-4 px-4 sm:mx-0 sm:px-0">
          <div className="flex sm:grid sm:grid-cols-2 lg:grid-cols-4 gap-4 overflow-x-auto snap-x snap-mandatory pb-4 sm:pb-0 no-scrollbar pt-1">
            {[
              { tier: 'Free', price: '$0', unit: '100 calls/mo', color: 'emerald', highlight: false, features: ['100 API calls/month', 'All endpoints', 'No credit card', 'Community support'], cta: 'Get Started Free', to: '/signup' },
              { tier: 'Standard', price: '$0.12', unit: 'per call · 101–10K', color: 'emerald', highlight: true, features: ['100 free included', 'Pay-as-you-go', 'Full API access', 'Email support'], cta: 'Start Building', to: '/signup' },
              { tier: 'Volume', price: '$0.10', unit: 'per call · 10K–100K', color: 'amber', highlight: false, features: ['Auto tier pricing', 'Priority support', 'Usage analytics', 'Account rep'], cta: 'Get Started', to: '/signup' },
              { tier: 'Enterprise', price: '$0.08', unit: 'per call · 100K+', color: 'slate', highlight: false, features: ['Best per-call rate', 'Custom SLAs', 'Dedicated support', 'Custom integrations'], cta: 'Contact Sales', href: 'mailto:sales@communityguardapi.com?subject=Enterprise%20API%20Inquiry' },
            ].map((t) => (
              <div
                key={t.tier}
                className={`flex-shrink-0 w-[75vw] sm:w-auto snap-center rounded-xl ${
                  t.highlight
                    ? 'border-2 border-emerald-500 bg-slate-800/80 p-5 sm:p-6 pt-3 sm:pt-4'
                    : 'border border-slate-700 bg-slate-800/50 p-5 sm:p-6'
                }`}
              >
                {t.highlight && (
                  <div className="bg-emerald-500 text-slate-900 text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wider text-center mb-4 mx-auto w-fit">
                    Most Popular
                  </div>
                )}
                <div className={`text-${t.color === 'slate' ? 'slate-300' : t.color + '-500'} text-xs font-semibold uppercase tracking-wider mb-2`}>{t.tier}</div>
                <div className="text-3xl sm:text-4xl font-bold mb-0.5">{t.price}</div>
                <div className="text-slate-400 text-xs sm:text-sm mb-5">{t.unit}</div>
                <ul className="space-y-2 text-xs sm:text-sm text-slate-300 mb-5">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-center gap-2">
                      <span className="text-green-400 text-xs">&#10003;</span> {f}
                    </li>
                  ))}
                </ul>
                {t.to ? (
                  <Link
                    to={t.to}
                    className={`block w-full text-center px-4 py-2.5 rounded-lg text-sm font-semibold transition ${
                      t.highlight
                        ? 'bg-emerald-500 hover:bg-emerald-600 text-white shadow-lg'
                        : `border ${t.color === 'amber' ? 'border-amber-400 text-amber-400 hover:bg-amber-400/10' : 'border-emerald-500 text-emerald-500 hover:bg-emerald-500/10'}`
                    }`}
                  >
                    {t.cta}
                  </Link>
                ) : (
                  <a
                    href={t.href}
                    className="block w-full text-center px-4 py-2.5 border border-slate-600 text-slate-300 hover:text-white hover:border-slate-500 rounded-lg text-sm font-medium transition"
                  >
                    {t.cta}
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Cost Calculator */}
        <div className="bg-slate-800/80 backdrop-blur-sm border border-slate-700 rounded-xl p-5 sm:p-8 mb-16">
          <h2 className="text-xl sm:text-2xl font-bold text-white mb-5 text-center">Cost Calculator</h2>
          <div className="max-w-xl mx-auto space-y-5">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Monthly API Calls</span>
                <span className="text-lg font-bold text-emerald-400 tabular-nums">{calls.toLocaleString()}</span>
              </div>
              <input
                type="range"
                min="0"
                max="200000"
                step="100"
                value={calls}
                onChange={(e) => setCalls(parseInt(e.target.value))}
                className="w-full h-1.5 bg-slate-700 rounded-full appearance-none cursor-pointer accent-emerald-500"
              />
              <div className="flex justify-between text-[10px] text-slate-600 mt-1">
                <span>0</span><span>50K</span><span>100K</span><span>150K</span><span>200K</span>
              </div>
            </div>

            <div className="flex items-center justify-between gap-2">
              {[
                { label: 'Monthly Cost', value: `$${estimatedCost.toFixed(2)}`, color: 'text-emerald-400' },
                { label: 'Free Calls', value: Math.min(calls, 100), color: 'text-green-400' },
                { label: 'Eff. Rate', value: `$${effectiveRate}`, color: 'text-white' },
              ].map(({ label, value, color }) => (
                <div key={label} className="flex-1 bg-slate-700/40 rounded-lg p-3 text-center">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
                  <div className={`text-xl sm:text-2xl font-bold tabular-nums mt-0.5 ${color}`}>{value}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Comparison — cards on mobile, table on desktop */}
        <div className="mb-16">
          <h2 className="text-xl sm:text-2xl font-bold text-white mb-6 text-center">How We Compare</h2>

          {/* Mobile: stacked cards */}
          <div className="sm:hidden space-y-2">
            {[
              { feature: 'Per-call pricing', us: '$0.08–$0.12', them: '$0.10–$0.25', win: true },
              { feature: 'Free tier', us: '100/mo', them: 'None', win: true },
              { feature: 'Coverage', us: '50 states', them: 'Varies', win: true },
              { feature: 'Conviction data', us: 'Included', them: 'Included', win: false },
              { feature: 'Geospatial search', us: 'Built-in', them: 'Limited', win: true },
              { feature: 'REST API', us: 'Yes', them: 'Varies', win: true },
              { feature: 'Setup fee', us: '$0', them: '$50–$500', win: true },
            ].map(({ feature, us, them, win }) => (
              <div key={feature} className="flex items-center justify-between bg-slate-800/60 border border-slate-700/50 rounded-lg px-4 py-3">
                <span className="text-xs text-slate-400 flex-1">{feature}</span>
                <span className={`text-xs font-bold flex-1 text-center ${win ? 'text-emerald-400' : 'text-slate-300'}`}>{us}</span>
                <span className="text-xs text-slate-500 flex-1 text-right">{them}</span>
              </div>
            ))}
            <div className="flex items-center justify-between px-4 text-[10px] uppercase tracking-wider text-slate-600 font-semibold">
              <span className="flex-1">Feature</span>
              <span className="flex-1 text-center text-emerald-600">Us</span>
              <span className="flex-1 text-right">Others</span>
            </div>
          </div>

          {/* Desktop: table */}
          <table className="hidden sm:table w-full max-w-3xl mx-auto">
            <thead>
              <tr className="border-b border-slate-700">
                <th className="py-3 px-4 text-left text-slate-400 font-medium text-sm">Feature</th>
                <th className="py-3 px-4 text-center text-emerald-500 font-semibold text-sm">CommunityGuardAPI</th>
                <th className="py-3 px-4 text-center text-slate-400 font-medium text-sm">Competitors</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/50">
              {[
                ['Per-call pricing', '$0.08 – $0.12', '$0.10 – $0.25', true],
                ['Free tier', '100/month', 'None', true],
                ['Nationwide coverage', '50 states', 'Varies', true],
                ['Conviction data', 'Included', 'Included', false],
                ['Geospatial search', 'Built-in', 'Limited', true],
                ['Modern REST API', 'Yes', 'Varies', true],
                ['Setup fee', '$0', '$50 – $500', true],
              ].map(([feat, us, them, win]) => (
                <tr key={feat}>
                  <td className="py-3 px-4 text-slate-300 text-sm">{feat}</td>
                  <td className={`py-3 px-4 text-center font-semibold text-sm ${win ? 'text-emerald-400' : 'text-green-400'}`}>{us}</td>
                  <td className={`py-3 px-4 text-center text-sm ${them === 'None' || them.includes('$50') ? 'text-red-400' : 'text-slate-400'}`}>{them}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* What's Included */}
        <div className="mb-16">
          <h2 className="text-xl sm:text-2xl font-bold text-white mb-6 text-center">What Every API Call Returns</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {[
              { label: 'Full Name & Aliases', icon: '👤' },
              { label: 'Photo & Physical Description', icon: '📸' },
              { label: 'Registered Address & Coordinates', icon: '📍' },
              { label: 'Conviction History & Victim Details', icon: '⚖️' },
              { label: 'Risk Level & Designation', icon: '🛡️' },
              { label: 'Registration Status & Dates', icon: '📋' },
            ].map(({ label, icon }) => (
              <div key={label} className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-4 text-center">
                <div className="text-2xl mb-2">{icon}</div>
                <div className="text-xs text-slate-300 font-medium">{label}</div>
              </div>
            ))}
          </div>
          <p className="text-center text-slate-500 text-xs mt-4">
            580K+ records across all 50 states. Data refreshed monthly from official state registries.
          </p>
        </div>

        {/* FAQ */}
        <div className="max-w-3xl mx-auto mb-16">
          <h2 className="text-2xl font-bold text-white mb-8 text-center">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {[
              {
                q: 'What counts as an API call?',
                a: 'Each request to our data endpoints (/api/data/search/, /api/data/offenders/, /api/data/offenders/nearby/) counts as one API call. Requests to auth, billing, or stats endpoints are not counted.',
              },
              {
                q: 'Do I need a credit card to start?',
                a: 'No. You get 100 free API calls per month with no credit card required. Add a payment method only when you need more than 100 calls.',
              },
              {
                q: 'How does tiered pricing work?',
                a: 'Pricing is calculated per-call with volume discounts. Your first 100 calls are free. Calls 101-10,000 are $0.12 each. Calls 10,001-100,000 are $0.10 each. Beyond 100K is $0.08 each.',
              },
              {
                q: 'What data is included per call?',
                a: 'Every API response includes full name, aliases, date of birth, physical description, photo URL, registered address with lat/lng coordinates, conviction history with victim details, risk level, registration status, and dates. All for the same per-call price.',
              },
              {
                q: 'How often is the data updated?',
                a: 'We refresh data monthly from all 50 state registries. Each record is scraped directly from the official state source — not a third-party reseller.',
              },
              {
                q: 'When am I billed?',
                a: 'Usage is aggregated monthly and invoiced on the 1st of each month for the previous month. You only pay for calls above the free tier.',
              },
              {
                q: 'Can I cancel anytime?',
                a: 'Yes. There are no contracts or commitments. Simply stop making API calls. You can revoke your API keys anytime from the dashboard.',
              },
              {
                q: 'What about uptime and reliability?',
                a: 'The API runs on AWS infrastructure. Rate limit is 60 requests/minute per API key. Need higher throughput? Contact us about enterprise plans.',
              },
            ].map((faq, i) => (
              <div key={i} className="bg-slate-800/50 border border-slate-700 rounded-lg p-5">
                <h3 className="text-white font-semibold mb-2">{faq.q}</h3>
                <p className="text-slate-400 text-sm">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center bg-gradient-to-r from-slate-900/50 to-emerald-500/20 border border-emerald-500/30 rounded-xl p-10">
          <h2 className="text-3xl font-bold text-white mb-4">Ready to Get Started?</h2>
          <p className="text-slate-300 mb-8 max-w-lg mx-auto">
            Create a free account and start querying nationwide sex offender data in minutes.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/signup"
              className="px-8 py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-lg shadow-lg transition"
            >
              Create Free Account
            </Link>
            <Link
              to="/api-docs"
              className="px-8 py-3 border border-slate-600 text-slate-300 hover:text-white hover:border-slate-500 rounded-lg font-medium transition"
            >
              Read the Docs
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PricingPage;
